import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Section variant styles using Class Variance Authority (CVA)
 *
 * Defines visual variants, gap, padding, and rounded corner options for the Section component.
 * All styles use design tokens from the HEN Design System.
 *
 * ## Variants
 * - default: No background styling
 * - light: Light gray background (bg-neutral-50)
 * - dark: Dark background (bg-neutral-900) with white text
 * - white-outline: White background with border (bg-white border-neutral-200)
 *
 * ## Gap Options (internal spacing between children)
 * - xxs through 3xl, plus max (80px) matching design tokens
 *
 * ## Padding Options
 * - none: No padding
 * - sm: Small padding
 * - md: Medium padding
 * - lg: Large padding
 * - section: Section-specific padding tokens (px-section-sides py-section-top-bottom)
 */
export const sectionVariants = cva('flex flex-col', {
  variants: {
    variant: {
      default: '',
      light: 'bg-neutral-50',
      dark: 'bg-neutral-900 text-white',
      'white-outline': 'bg-white border border-neutral-200',
    },
    gap: {
      xxs: 'gap-xxs',
      xs: 'gap-xs',
      sm: 'gap-sm',
      md: 'gap-md',
      lg: 'gap-lg',
      xl: 'gap-xl',
      '2xl': 'gap-2xl',
      '3xl': 'gap-3xl',
      max: 'gap-max',
    },
    padding: {
      none: '',
      section: 'px-section-sides py-section-top-bottom',
    },
    rounded: {
      true: 'rounded-xl',
      false: '',
      none: '',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
    },
  },
  defaultVariants: {
    variant: 'default',
    gap: '3xl',
    padding: 'section',
    rounded: false,
  },
});

export type SectionVariantProps = VariantProps<typeof sectionVariants>;
